class SlideshowCustom extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.autoplayInterval = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    this.startX = null;
    this.currentX = null;
    this.justDragged = false;
  }

  connectedCallback() {
    this.slideItems = this.querySelectorAll('.slideshow-custom__slide');
    this.dots = this.querySelectorAll('.slideshow-custom__dot');
    this.autoplay = this.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 5000;
    this.totalSlides = this.slideItems.length;

    if (this.totalSlides <= 1) return;

    this.slideItems[0].classList.add('slideshow-custom__slide--active');
    this.ensureSlideLoaded(0, true);
    this.ensureSlideLoaded(1);

    this.bindEvents();
    if (this.autoplay) this.startAutoplay();
  }

  ensureSlideLoaded(index, highPriority = false) {
    const slide = this.slideItems[index];
    if (!slide) return;

    slide.querySelectorAll('source[data-srcset]').forEach((source) => {
      source.setAttribute('srcset', source.dataset.srcset);
      source.removeAttribute('data-srcset');
    });

    const image = slide.querySelector('img');
    if (image) {
      if (image.dataset && image.dataset.src) {
        image.setAttribute('src', image.dataset.src);
        image.removeAttribute('data-src');
      }

      if (highPriority) {
        image.setAttribute('loading', 'eager');
        image.setAttribute('decoding', 'sync');
        image.setAttribute('fetchpriority', 'high');
      }
    }
  }

  bindEvents() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goTo(index));
    });
    
    // Touch events for mobile
    this.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    
    // Mouse events for desktop
    this.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.addEventListener('mouseup', (e) => this.handleMouseUp(e));
    this.addEventListener('click', (e) => this.handleClick(e), true); // Use capture
    this.addEventListener('mouseleave', (e) => {
      this.handleMouseLeave(e);
      this.resumeAutoplay();
    });
    
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
  }

  handleClick(e) {
    // If we just dragged, prevent the click
    if (this.justDragged) {
      e.preventDefault();
      e.stopPropagation();
      this.justDragged = false;
    }
  }

  handleTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
    this.pauseAutoplay();
  }

  handleTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    this.handleSwipe();
    setTimeout(() => this.resumeAutoplay(), 3000);
  }

  handleMouseDown(e) {
    // Only ignore buttons and content buttons (not slide-link)
    const isButton = e.target.closest('button');
    const isContentButton = e.target.closest('a.slideshow-custom__button');
    
    if (isButton || isContentButton) return;
    
    this.isDragging = false;
    this.startX = e.pageX;
    this.currentX = e.pageX;
    this.pauseAutoplay();
    // Don't prevent default here - let link clicks work
  }

  handleMouseMove(e) {
    if (this.startX === null) return;
    
    this.currentX = e.pageX;
    const diff = Math.abs(this.startX - this.currentX);
    
    // Mark as dragging if moved more than 5px
    if (diff > 5 && !this.isDragging) {
      this.isDragging = true;
      this.classList.add('is-dragging');
      this.style.cursor = 'grabbing';
      // Prevent text selection while dragging
      e.preventDefault();
    }
  }

  handleMouseUp(e) {
    if (this.startX === null) return;
    
    const wasDragging = this.isDragging;
    const diff = this.startX - this.currentX;
    
    this.classList.remove('is-dragging');
    this.style.cursor = '';
    this.startX = null;
    this.isDragging = false;
    
    // Only change slide if actually dragged
    if (wasDragging) {
      this.justDragged = true;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      
      // Restart autoplay after drag
      if (this.autoplay && !this.autoplayInterval) {
        setTimeout(() => this.resumeAutoplay(), 3000);
      }
      
      // Reset flag after a short delay
      setTimeout(() => {
        this.justDragged = false;
      }, 10);
    } else {
      this.justDragged = false;
    }
  }

  handleMouseLeave(e) {
    if (this.startX !== null) {
      this.handleMouseUp(e);
    }
  }

  handleSwipe() {
    const diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.next();
      } else {
        this.prev();
      }
    }
  }

  goTo(index) {
    this.currentIndex = index;
    this.updateSlide();
  }

  prev() {
    this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.updateSlide();
  }

  next() {
    this.currentIndex = (this.currentIndex + 1) % this.totalSlides;
    this.updateSlide();
  }

  updateSlide() {
    this.ensureSlideLoaded(this.currentIndex, true);
    this.ensureSlideLoaded((this.currentIndex + 1) % this.totalSlides);

    this.slideItems.forEach((slide, index) => {
      slide.classList.toggle('slideshow-custom__slide--active', index === this.currentIndex);
    });
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('slideshow-custom__dot--active', index === this.currentIndex);
    });
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => this.next(), this.autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resumeAutoplay() {
    if (this.autoplay && !this.autoplayInterval) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
  }
}

customElements.define('slideshow-custom', SlideshowCustom);
